import * as React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import {
  getAgentPathPrefix, instance, TriggerConditionsRender
} from '@lib/index';
import {
  DiffStates,
  ReferenceNode,
  StateUpdateInfo,
  Attribute,
  FrameItem,
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  FrameType,
  ProviderType,
  StateUpdateTriggerMethod,
  StateUpdateUpdateFuncArgumentConfig
} from '@interface/index';
import { SourceType } from '@config/index';
import { NiTable, sourceRender } from '@component/index';
import { localizedString } from '@locale/index';
import EmptyLine from '@component/business-component/empty-line/empty-line';

export interface StateUpdateTableProps {
  history: any;
  title: string;
  frame: FrameItem;
  availableIntents: IntentInfo[];
  availableComposites: CompositeInfo[];
  availableEntities: EntityInfo[];
  disabled?: boolean;
  tag: string;
  data: StateUpdateInfo[];
  fallback?: boolean;
  providerType?: ProviderType;
  selectedParameters: Attribute[];
  onChange: (args: StateUpdateUpdateFuncArgumentConfig) => void;
  onAdd?: () => void;
}
export interface StateUpdateTableState {
  visible: boolean;
  cur: number;
  isNew: boolean;
  filterData: StateUpdateInfo[];
}

export class StateUpdateTableComp extends React.Component<
  StateUpdateTableProps,
  StateUpdateTableState
> {
  public state: StateUpdateTableState = {
    visible: false,
    cur: -1,
    isNew: false,
    filterData: []
  };

  componentDidMount() {
    instance.on(`@addStateUpdateInfo${this.props.tag}`, (args: any) => {
      this.setState({
        cur: 0,
        visible: true,
        isNew: args && args.isNew
      });
    });

    const filterData = this.props.fallback
      ? this.props.data.filter(item => item.triggerMethod === StateUpdateTriggerMethod.Fallback)
      : this.props.data.filter(item => item.triggerMethod !== StateUpdateTriggerMethod.Fallback);

    this.setState({
      filterData
    });

  }
  componentWillUnmount() {
    instance.off(`@addStateUpdateInfo${this.props.tag}`);
  }

  componentDidUpdate(prevProps: StateUpdateTableProps) {
    if (prevProps.data !== this.props.data || prevProps.fallback !== this.props.fallback) {

      const filterData = this.props.fallback
        ? this.props.data.filter(item => item.triggerMethod === StateUpdateTriggerMethod.Fallback)
        : this.props.data.filter(item => item.triggerMethod !== StateUpdateTriggerMethod.Fallback);

      this.setState({
        filterData
      });

    }
  }

  render() {
    const props = this.props;
    const {
      frame, data
    } = this.props;

    return (
      <>{this.state.filterData && this.state.filterData.filter(item => !item.toBeDeleted).length &&
        <NiTable
          className="db-table"
          rowKey={(record: StateUpdateInfo) => record.id || ''}
          dataSource={this.state.filterData}
          columns={this.getColumns()}
          onRow={(record: StateUpdateInfo, index?: number) => {

            return {
              onClick: () => {

                if (
                  record.state !== DiffStates.DEL &&
                  record.state !== DiffStates.MASTER_CONFLICT
                ) {

                  props.history.push(`${getAgentPathPrefix()}/${frame.type === FrameType.intent
                    ? 'intent'
                    : 'frame'}/${frame.id}/state_update/${record.id}${record.inheritedFromCompositeId
                      ? `?super_id=${record.inheritedFromCompositeId}`
                      : ''}`);
                }
              }
            };
          }}
          pagination={false}
        /> || <EmptyLine text={props.fallback ? 'Click to add a fallback' : 'Click to add a transition'} onClick={this.props.onAdd} />
      }
      </>
    );
  }

  getColumns = () => {
    const props = this.props;

    const nameColumn = {
      title: 'Name',
      dataIndex: 'name',
    };
    const scopeColumn = {
      // width: '90%',
      title: localizedString('state_update_trigger'),
      key: 'satateConfig',
      render: (_: any, record: StateUpdateInfo, index: number) => {
        switch (record.triggerMethod) {
          case StateUpdateTriggerMethod.State:
            return (
              <div className="table-name-area text">{
                (!record.stateConfig || !record.stateConfig.slot || !record.stateConfig.slot.length || !record.stateConfig.state || !record.stateConfig.state.length)
                  ? <span>
                    State undefined
                  </span> :
                  <span>{record.stateConfig &&
                    record.stateConfig.slot &&
                    record.stateConfig.slot.map((item: ReferenceNode) => item.name).join('.')} {record.stateConfig &&
                      record.stateConfig.state}</span>
              }
              </div>
            );
          case StateUpdateTriggerMethod.Event:
            return (
              <div className="table-name-area text">
                <span>{record.event && record.event.name || 'Event undefined'}</span>
              </div>
            );
          case StateUpdateTriggerMethod.Fallback:
            return 'fallback';
          default:
            return (
              <div className="content-wrap">
                {record.conditionType === SourceType.REGULAR
                  ? (record.conditions.length > 0
                    ? <TriggerConditionsRender data={record.conditions} />
                    : <span className="condition-undefined">Condition undefined</span>
                  ) : (record.conditionCode.length
                    ? <span>{record.conditionCode}</span>
                    : <span className="condition-undefined">Condition undefined</span>
                  )
                }
              </div>
            );
        }
      }
    };

    const sourceColumn = {
      width: 150,
      title: 'Source',
      dataIndex: 'inheritedFromCompositeName',
      render: (name: string, record: StateUpdateInfo, index: number) => {
        return sourceRender(record, props.frame.type);
      }
    };

    const adminColumn = {
      title: '',
      className: 'hide',
      dataIndex: 'admin',
      width: 70,
      render: (admin: any, record: StateUpdateInfo, index: number) => {
        return (
          <div className="icon-area">
            {
              !props.disabled &&
              !record.originKey &&
              !record.inheritedFromCompositeId &&
              (
                <DeleteOutlined
                  className="delete-icon"
                  onClick={async (e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();

                    const realIndex = props.data.indexOf(record);
                    props.data.splice(realIndex, 1);

                    props.onChange({
                      nextStateUpdates: props.data,
                      nextDelStateUpdate: record
                    });
                  }} />
              )}
          </div>
        );
      }
    };

    return [
      nameColumn,
      scopeColumn,
      sourceColumn,
      adminColumn
    ];
  };

  handleConfirm = (next: any) => {
    const state = this.state;
    const props = this.props;
    next.dirty = true;
    this.props.data[state.cur] = next;
    this.props.onChange({
      nextStateUpdates: props.data
    });
    this.setState({
      visible: false,
      cur: -1
    });
  };

  handleClose = () => {
    const state = this.state;
    const props = this.props;

    if (state.isNew) {
      props.data.splice(this.state.cur, 1);
    }

    this.setState({
      visible: false,
      cur: -1
    });
  };
}
